import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import KBContentDto from '@/models/KnowledgeBase/KBContentDto';
import KBTOCDto from '@/models/KnowledgeBase/KBTOCDto';
import KBContentList from '@/models/KnowledgeBase/KBContentList';
import KBSearchResultDto from '@/models/KnowledgeBase/KBSearchResultDto';

export interface IAgencyKBState {
  loadingTOC: boolean;
  toc: KBTOCDto | null;
  loadingContentList: boolean;
  contentList: KBContentList | null;
  loadingContent: boolean;
  currentContent: KBContentDto | null;
  loadingSearch: boolean;
  searchResults: KBSearchResultDto | null;
}

@Module({ dynamic: true, store, name: 'AgencyKB', namespaced: true })
class AgencyKB extends VuexModule implements IAgencyKBState {

  public loadingTOC = false;
  public toc: KBTOCDto | null = null;
  public loadingContentList = false;
  public contentList: KBContentList | null = null;
  public loadingContent = false;
  public currentContent: KBContentDto | null = null;
  public loadingSearch = false;
  public searchResults: KBSearchResultDto | null = null;

  // TOC Actions - Read Only
  @Action
  async getTOC() {
    this.setLoadingTOC(true);
    return ajax.get('/api/v1/agency/kb/toc')
      .then(response => {
        this.context.commit('setTOC', response.data);
        this.setLoadingTOC(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingTOC(false);
        throw error;
      });
  }

  // Content List Actions - Read Only (Published content only)
  @Action
  async getContentList(params?: { category?: string; status?: string; parentId?: string; limit?: number; offset?: number; sort?: string }) {
    this.setLoadingContentList(true);
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.parentId) queryParams.append('parentId', params.parentId);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.sort) queryParams.append('sort', params.sort);

    const url = '/api/v1/agency/kb/content' + (queryParams.toString() ? '?' + queryParams.toString() : '');

    return ajax.get(url)
      .then(response => {
        this.context.commit('setContentList', response.data);
        this.setLoadingContentList(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingContentList(false);
        throw error;
      });
  }

  // Get Single Content - Read Only
  @Action
  async getContent(contentId: string) {
    this.setLoadingContent(true);
    return ajax.get(`/api/v1/agency/kb/content/${contentId}`)
      .then(response => {
        this.context.commit('setCurrentContent', response.data);
        this.setLoadingContent(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingContent(false);
        throw error;
      });
  }

  // Search Actions - Read Only
  @Action
  async search(params: { query: string; category?: string; semantic?: boolean; limit?: number }) {
    this.setLoadingSearch(true);
    const queryParams = new URLSearchParams();
    queryParams.append('q', params.query);
    if (params.category) queryParams.append('category', params.category);
    if (params.semantic !== undefined) queryParams.append('semantic', params.semantic.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    return ajax.get(`/api/v1/agency/kb/search?${queryParams.toString()}`)
      .then(response => {
        this.context.commit('setSearchResults', response.data);
        this.setLoadingSearch(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingSearch(false);
        throw error;
      });
  }

  @Action
  clearSearch() {
    this.context.commit('setSearchResults', null);
  }

  // Utility Actions
  @Action
  cleanState() {
    this.context.commit('resetState');
  }

  // Mutations
  @Mutation
  resetState() {
    this.toc = null;
    this.contentList = null;
    this.currentContent = null;
    this.searchResults = null;
    this.loadingTOC = false;
    this.loadingContentList = false;
    this.loadingContent = false;
    this.loadingSearch = false;
  }

  @Mutation
  private setLoadingTOC(loading: boolean) {
    this.loadingTOC = loading;
  }

  @Mutation
  private setTOC(toc: KBTOCDto) {
    this.toc = toc;
  }

  @Mutation
  private setLoadingContentList(loading: boolean) {
    this.loadingContentList = loading;
  }

  @Mutation
  private setContentList(list: KBContentList) {
    this.contentList = list;
  }

  @Mutation
  private setLoadingContent(loading: boolean) {
    this.loadingContent = loading;
  }

  @Mutation
  private setCurrentContent(content: KBContentDto) {
    this.currentContent = content;
  }

  @Mutation
  private setLoadingSearch(loading: boolean) {
    this.loadingSearch = loading;
  }

  @Mutation
  private setSearchResults(results: KBSearchResultDto | null) {
    this.searchResults = results;
  }
}

export const AgencyKBModule = getModule(AgencyKB);
